import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { Fragment, FC } from 'react'
import { Col, Row, Table } from 'reactstrap'

import { Partner } from 'common/api/types'
import { FilterTagsComponent as FilterTags, SortFilterTh, Spinner } from 'common/components'
import { DatePickerInput, TextFilterInput } from 'common/components/FilterInput'
import { PopoverItemView } from 'common/components/PopoverItemView'
import { PRODUCT_NAMES, ADVICE_REQUESTS_ENDPOINT, FIELDS_OF_LAW_ENTRIES } from 'common/constants'
import { ListProps, withList } from 'common/container'
import { LoggedInUser } from 'common/user-context'
import { convertToTagValues, formatDate, formatDateTime } from 'common/utils'

import query from '../graphql/adviceRequests.graphql'
import { LawyerRequest } from '../interfaces'
import { ChancelleryLocationsQuery, FilterOperator, SortDirection } from '../interfaces/schemaDefinition'
import { getChancelleryFilter } from '../utils'

import { AdviceMenuButton } from './AdviceMenu'
import { PanelTabs } from './PanelTabs/PanelTabs'
import { PartnerCell } from './PartnerCell'
import { ToolbarTabs } from './ToolbarTabs'

interface PotentialMandatesProps extends ListProps<LawyerRequest> {
  user?: LoggedInUser
  chancelleryLocations: ChancelleryLocationsQuery['chancelleryLocations']['list']
  assignableUsers: Array<{ id: string; name: string }>
  assignFilter?: string
  onRowClick: (rowId: string) => React.EventHandler<React.FormEvent<HTMLTableRowElement>>
  partners: Array<Partner>
  hasPremium: boolean
}

const createOnSort = (func: (id: string) => void, id: string) => () => func(id)

export const PotentialMandatesView: FC<PotentialMandatesProps> = ({
  partners,
  assignableUsers,
  list,
  sort,
  page,
  pageSize,
  total,
  filters,
  onRowClick,
  onSort,
  onFilterChange,
  onFilterRemove,
  onNext,
  onPrev,
  onReload,
  loading,
  hasPremium,
}) => (
  <Fragment>
    <PanelTabs>
      <ToolbarTabs
        pagination={{ page, pageSize, total, label: 'Wiedervorlagen' }}
        onNext={onNext}
        onPrev={onPrev}
        title="Wiedervorlagen"
      />
      <Row className="m-b-20">
        <Col sm="12">
          <Spinner condition={loading} center>
            <FilterTags filters={filters} onTagRemove={onFilterRemove} partners={partners} />
            {list.length > 0 ? (
              <Table hover responsive>
                <thead>
                  <tr>
                    <SortFilterTh
                      id="potentialAdviceId"
                      name="adviceId"
                      sort={sort}
                      onSort={createOnSort(onSort, 'adviceId')}
                      onFilterSubmit={onFilterChange}
                      FilterInput={TextFilterInput}
                      placeholder="Beratungs ID..."
                    >
                      ID
                    </SortFilterTh>
                    {hasPremium && <th>Produkt</th>}
                    <SortFilterTh
                      id="potentialPersonName"
                      name="personName"
                      sort={sort}
                      onSort={createOnSort(onSort, 'personName')}
                      onFilterSubmit={onFilterChange}
                      FilterInput={TextFilterInput}
                      placeholder="Name..."
                    >
                      Name
                    </SortFilterTh>
                    <SortFilterTh
                      id="potentialReminderDate"
                      name="reminderDate"
                      sort={sort}
                      onSort={createOnSort(onSort, 'reminderDate')}
                      onFilterSubmit={onFilterChange}
                      FilterInput={DatePickerInput}
                      placeholder="Termin..."
                    >
                      Termin
                    </SortFilterTh>
                    <SortFilterTh
                      id="potentialFieldOfLaw"
                      name="fieldOfLaw"
                      sort={sort}
                      onSort={createOnSort(onSort, 'fieldOfLaw')}
                      onFilterSubmit={onFilterChange}
                      suggestions={convertToTagValues(FIELDS_OF_LAW_ENTRIES)}
                      FilterInput={TextFilterInput}
                      placeholder="Rechtsgebiet..."
                    >
                      Rechtsgebiet
                    </SortFilterTh>
                    <SortFilterTh
                      id="potentialPartnerId"
                      name="partnerId"
                      sort={sort}
                      onSort={createOnSort(onSort, 'partnerId')}
                      onFilterSubmit={onFilterChange}
                      suggestions={convertToTagValues(partners)}
                      FilterInput={TextFilterInput}
                      placeholder="Partner ID..."
                    >
                      Partner
                    </SortFilterTh>
                    <SortFilterTh
                      id="potentialChancellery"
                      name="chancellery"
                      sort={sort}
                      onSort={createOnSort(onSort, 'chancellery')}
                      onFilterSubmit={onFilterChange}
                      FilterInput={TextFilterInput}
                      placeholder="Kanzlei..."
                    >
                      Kanzlei
                    </SortFilterTh>
                    <SortFilterTh
                      id="potentialCreatedAt"
                      name="createdAt"
                      sort={sort}
                      onSort={createOnSort(onSort, 'createdAt')}
                      onFilterSubmit={onFilterChange}
                      FilterInput={DatePickerInput}
                      placeholder="Erstellt..."
                    >
                      Erstellt
                    </SortFilterTh>
                    <th>In Bearbeitung durch</th>
                    <th className="text-center action-header">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((adviceRequest, index) => (
                    <tr key={index} className="row-hover" onClick={onRowClick(adviceRequest.adviceId)}>
                      <td scope="row" className="text-center">
                        {adviceRequest.adviceId}
                      </td>
                      {hasPremium && <td>{adviceRequest.product ? PRODUCT_NAMES[adviceRequest.product.id] : 'Basic'}</td>}
                      <td className="text-center">
                        {adviceRequest.person.firstname} {adviceRequest.person.lastname}
                      </td>
                      <td className="text-center">
                        {!!adviceRequest.resultNotes ? (
                          <PopoverItemView content={adviceRequest.resultNotes}>
                            {adviceRequest.reminderDate ? formatDateTime(new Date(adviceRequest.reminderDate)) : '-'}
                          </PopoverItemView>
                        ) : (
                          <Fragment>
                            {adviceRequest.reminderDate ? formatDateTime(new Date(adviceRequest.reminderDate)) : '-'}
                          </Fragment>
                        )}
                      </td>
                      <td className="text-center">{adviceRequest.fieldOfLaw ? adviceRequest.fieldOfLaw.name : '-'}</td>
                      {adviceRequest.followsUp ? (
                        <PartnerCell partner={{ id: 'klugo', name: 'klugo' }} />
                      ) : (
                        <PartnerCell partner={adviceRequest.partner} />
                      )}
                      <td className="text-center">{adviceRequest.chancellery ? adviceRequest.chancellery.name : '-'}</td>
                      <td className="text-center">{formatDate(new Date(adviceRequest.createdAt))}</td>
                      <td className="text-center">
                        {adviceRequest.assignedTo && adviceRequest.assignedTo.name ? (
                          adviceRequest.assignedTo.name
                        ) : (
                          <span>
                            <FontAwesomeIcon icon={faExclamationTriangle} size="lg" className="inline-icon" /> Nicht zugewiesen
                          </span>
                        )}
                      </td>
                      <td className="text-center action-cell">
                        <AdviceMenuButton adviceRequest={adviceRequest} onAssignTo={onReload} assignableUsers={assignableUsers} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <h3 className="no-data">Keine neuen Wiedervorlagen</h3>
            )}
          </Spinner>
        </Col>
      </Row>
    </PanelTabs>
  </Fragment>
)

export const PotentialMandates = withList<LawyerRequest, PotentialMandatesProps>({
  query,
  endpoint: ADVICE_REQUESTS_ENDPOINT,
  responseKey: 'adviceRequests',
  searchPrefix: 'potential',
  pageSize: 100,
  queryMapper: (listState, props) => ({
    ...listState,
    sort: {
      sortBy: 'reminderDate',
      sortDirection: SortDirection.Asc,
    },
    filters: [
      ...listState.filters,
      ...(props.assignFilter
        ? [
            {
              name: 'assignedTo',
              operator: FilterOperator.Equals,
              value: props.assignFilter,
            },
          ]
        : []),
      getChancelleryFilter(props.user, props.chancelleryLocations),
      {
        name: 'product.type',
        operator: FilterOperator.Equals,
        value: 'CONSUMER',
      },
      {
        name: 'status',
        operator: FilterOperator.Equals,
        value: 'potential',
      },
    ],
  }),
})(PotentialMandatesView)
